import { useTranslation } from "react-i18next";
import { useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { toast } from "sonner";
import { Loader2, Trash2 } from "lucide-react";
import { Button } from "@/components/primitives/Button";
import { useAuth } from "@/lib/auth";
import { errorFrom } from "@/lib/errors";
import { deleteAccount, isDeletionBlocked } from "@/lib/billingApi";

// Danger zone for the Billing page: permanent self-service account deletion (blocked while a paid period is running).
export function DeleteAccountZone() {
  const { t } = useTranslation();
  const { logout } = useAuth();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);
  const [busy, setBusy] = useState(false);
  const [blocked, setBlocked] = useState(false);

  const confirm = async () => {
    setBusy(true);
    try {
      await deleteAccount();
      toast.success(t("account.deleted"));
      await logout();
      navigate("/", { replace: true });
    } catch (err) {
      if (isDeletionBlocked(err)) setBlocked(true);
      else toast.error(errorFrom(err, "account.deleteFailed"));
    } finally {
      setBusy(false);
    }
  };

  return (
    <section data-testid="account-danger-zone" className="mx-auto mt-24 max-w-4xl rounded-3xl border border-red-400/25 bg-red-950/10 p-6 sm:p-8">
      <p className="font-display text-[11px] uppercase tracking-[0.3em] text-red-300/80">{t("account.dangerEyebrow")}</p>
      <div className="mt-3 flex flex-col gap-4 sm:flex-row sm:items-start sm:justify-between">
        <div className="max-w-xl">
          <p className="font-display text-lg text-foreground">{t("account.deleteTitle")}</p>
          <p className="mt-1 text-sm leading-relaxed text-foreground/65">{t("account.deleteHint")}</p>
        </div>
        {!open && (
          <Button data-testid="account-delete-btn" variant="secondary" size="sm" className="shrink-0 border-red-400/40 text-red-200 hover:text-red-100" onClick={() => setOpen(true)}>
            <Trash2 className="h-3.5 w-3.5" /> {t("account.deleteTitle")}
          </Button>
        )}
      </div>
      {open && (
        <div data-testid="account-delete-confirm" className="mt-6 rounded-2xl border border-red-400/30 bg-black/30 p-5">
          <p className="text-sm font-semibold text-red-200">{t("account.confirmTitle")}</p>
          <p className="mt-2 text-sm leading-relaxed text-foreground/75">{t("account.confirmBody")}</p>
          {blocked && (
            <p data-testid="account-delete-blocked" className="mt-3 rounded-xl border border-[#FFC773]/30 bg-[#FFC773]/10 p-3 text-sm text-[#FFC773]">{t("account.blockedBySubscription")}</p>
          )}
          <div className="mt-4 flex flex-wrap gap-2">
            <Button data-testid="account-delete-cancel" variant="ghost" size="sm" onClick={() => { setOpen(false); setBlocked(false); }} disabled={busy}>{t("common.cancel")}</Button>
            <Button data-testid="account-delete-confirm-btn" size="sm" className="bg-red-500/90 text-white hover:bg-red-500" onClick={confirm} disabled={busy || blocked}>
              {busy ? <Loader2 className="h-3.5 w-3.5 animate-spin" /> : <Trash2 className="h-3.5 w-3.5" />} {t("account.confirmCta")}
            </Button>
          </div>
        </div>
      )}
      <p className="mt-5 text-xs text-foreground/50">
        <Link to="/privacy" data-testid="account-privacy-link" className="underline-offset-4 hover:underline">{t("legal.privacy")}</Link>
      </p>
    </section>
  );
}
